import createReducer from 'redux/createReducer';

import type { BasketBuilderExperienceSuggestionsByCategoryResponse } from 'api/definitions/basket-builder-experience/types';
import type { TransformedProducts } from 'redux/transforms/transform-products-and-recommendations-into-legacy-format';
import type { LegacyProductReference } from 'constants/products';

import { GET_MORE_OF_WHAT_YOU_LOVE_SUGGESTIONS } from 'redux/modules/more-of-what-you-love/actions/types';

import type {
  MoreOfWhatYouLoveState,
  MoreOfWhatYouLoveRequestState,
  MoreOfWhatYouLoveSuccessState,
} from 'redux/modules/more-of-what-you-love/types';

import { initialState } from '../state';

export type MoreOfWhatYouLoveSuggestionsFetcherResult = Pick<
  TransformedProducts<BasketBuilderExperienceSuggestionsByCategoryResponse>,
  'eligibility' | 'categories'
> & {
  suggestions: LegacyProductReference[];
  metadata: BasketBuilderExperienceSuggestionsByCategoryResponse['metadata'];
};

export type MoreOfWhatYouLoveSuggestionsRequestAction = {
  offersAndPromosOnly: boolean;
  pageNumber: number;
};

export type MoreOfWhatYouLoveSuggestionsSuccessAction = {
  result: MoreOfWhatYouLoveSuggestionsFetcherResult;
  offersAndPromosOnly: boolean;
  pageNumber: number;
};

const isSuggestionsSuccessfullyLoaded = (
  state: MoreOfWhatYouLoveState,
): state is MoreOfWhatYouLoveSuccessState => 'suggestions' in state;

const mergeCategories = (
  existingCategories: MoreOfWhatYouLoveSuccessState['categories'],
  newCategories: MoreOfWhatYouLoveSuccessState['categories'],
): MoreOfWhatYouLoveSuccessState['categories'] => {
  if (!newCategories.length) {
    return existingCategories;
  }

  const lastExisting = existingCategories[existingCategories.length - 1];
  const firstNew = newCategories[0];

  if (lastExisting && firstNew && lastExisting.id === firstNew.id) {
    const mergedCategory = {
      ...lastExisting,
      products: [...lastExisting.products, ...firstNew.products],
    };

    return [...existingCategories.slice(0, -1), mergedCategory, ...newCategories.slice(1)];
  }

  return [...existingCategories, ...newCategories];
};

const getMoreOfWhatYouLoveSuggestionsRequest = (
  state: MoreOfWhatYouLoveState,
  { pageNumber }: MoreOfWhatYouLoveSuggestionsRequestAction,
): MoreOfWhatYouLoveRequestState | MoreOfWhatYouLoveSuccessState => {
  return {
    ...state,
    loading: pageNumber === 1,
    loadingMore: pageNumber > 1,
  };
};

const getMoreOfWhatYouLoveSuggestionsSuccess = (
  state: MoreOfWhatYouLoveState,
  {
    result: { eligibility, categories, suggestions, metadata },
    offersAndPromosOnly,
    pageNumber,
  }: MoreOfWhatYouLoveSuggestionsSuccessAction,
): MoreOfWhatYouLoveSuccessState => {
  const isLoadMore = pageNumber > 1 && isSuggestionsSuccessfullyLoaded(state);
  const hasNoProducts = suggestions.length === 0;

  if (!isLoadMore) {
    return {
      ...state,
      loading: false,
      loadingMore: false,
      eligibility,
      categories,
      suggestions,
      hasOffersOrPromos: metadata?.hasOffersOrPromos ?? false,
      hasNextPage: metadata?.hasNextPage ?? false,
      pageNumber: metadata?.pageNumber ?? 1,
      offersAndPromosOnly,
    };
  }

  return {
    ...state,
    loading: false,
    loadingMore: false,
    eligibility,
    categories: mergeCategories(state.categories, categories),
    suggestions: [...state.suggestions, ...suggestions],
    hasOffersOrPromos: metadata?.hasOffersOrPromos ?? state.hasOffersOrPromos,
    hasNextPage: hasNoProducts ? false : (metadata?.hasNextPage ?? false),
    pageNumber: metadata?.pageNumber ?? state.pageNumber,
    offersAndPromosOnly,
  };
};

const actionTypeReducerMap = [
  [GET_MORE_OF_WHAT_YOU_LOVE_SUGGESTIONS.request, getMoreOfWhatYouLoveSuggestionsRequest],
  [GET_MORE_OF_WHAT_YOU_LOVE_SUGGESTIONS.success, getMoreOfWhatYouLoveSuggestionsSuccess],
] as const;

export type PayloadMap = ReducerMapTupleToRecord<typeof actionTypeReducerMap>;
export default createReducer(initialState, actionTypeReducerMap);
